import { useState } from 'react'
import { Link } from 'react-router'
import { ExternalLink, Info } from 'lucide-react'
import { DateValue, EmptyState, ErrorState, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useFeatureFlags, useFiscalYears, usePayrollCalendar, useSequences, useSettingsMutations,
} from '../api'
import { DirectoryPrivacyCard } from '../components/DirectoryPrivacyCard'
import { LocalisationCard } from '../components/LocalisationCard'

/**
 * The settings hub.
 *
 * <p>Localisation, branding and custom fields already had screens; this is the page that says
 * where everything is and edits what had nowhere to live — flags, the fiscal year, the payroll
 * calendar and numbering.
 */
export function SettingsHubPage() {
  useBreadcrumb()
  const flags = useFeatureFlags()
  const years = useFiscalYears()
  const calendar = usePayrollCalendar()
  const sequences = useSequences()
  const actions = useSettingsMutations()

  const [error, setError] = useState<string | null>(null)
  const [sequenceDraft, setSequenceDraft] = useState<Record<string, string>>({})

  return (
    <div className="space-y-4">
      <PageHeader
        title="Settings"
        subtitle="How this company writes things down, and what it has switched on."
      />

      {error && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <DirectoryPrivacyCard />
        <LocalisationCard />

        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">
            Everything else with a screen
          </h3>
          <ul className="space-y-2 text-sm">
            {([
              ['Branding', '/settings/branding'],
              ['Custom fields', '/settings/custom-fields'],
              ['Notifications', '/settings/notifications'],
              ['Banks', '/settings/banks'],
              ['User roles & access', '/settings/grants'],
              ['Segregation of duties', '/settings/segregation-of-duties'],
              ['AI & assistant', '/settings/ai'],
              ['Approval workflows', '/administration/workflows'],
            ] as const).map(([label, to]) => (
              <li key={to}>
                <Link
                  to={to}
                  className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                >
                  {label}
                  <ExternalLink className="size-3" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card>
        <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
          Feature flags
        </h3>
        {flags.isPending && <Skeleton className="m-4 h-20" />}
        {flags.isError && <ErrorState error={flags.error} onRetry={() => void flags.refetch()} />}
        {flags.isSuccess && (
          <ul className="divide-y divide-[var(--color-border)]">
            {flags.data.map((flag) => (
              <li key={flag.key} className="flex flex-wrap items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-heading)]">{flag.label}</p>
                  <p className="text-xs text-[var(--color-muted)]">{flag.description}</p>
                  {flag.enabled !== flag.defaultOn && (
                    <p className="text-xs text-[var(--color-body)]">
                      Changed from the shipped default, which is {flag.defaultOn ? 'on' : 'off'}.
                    </p>
                  )}
                </div>
                <label className="flex shrink-0 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={flag.enabled}
                    onChange={(event) => {
                      setError(null)
                      actions.setFlag.mutate(
                        { key: flag.key, enabled: event.target.checked, note: null },
                        {
                          onError: (caught) => setError(
                            toApiProblem(caught)?.detail ?? 'That flag could not be changed'),
                        })
                    }}
                  />
                  {flag.enabled ? 'On' : 'Off'}
                </label>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
            Fiscal years
          </h3>
          {years.isPending && <Skeleton className="m-4 h-16" />}
          {years.isSuccess && years.data.length === 0 && (
            <EmptyState
              title="No fiscal year"
              description="A year with a start and an end, and the flag that says nothing may be posted into it any more."
            />
          )}
          {years.isSuccess && years.data.length > 0 && (
            <ul className="divide-y divide-[var(--color-border)]">
              {years.data.map((year) => (
                <li key={year.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <div>
                    <p className="font-medium text-[var(--color-heading)]">{year.name}</p>
                    <p className="text-xs text-[var(--color-muted)]">
                      <DateValue value={year.startDate} /> – <DateValue value={year.endDate} />
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge
                      status={year.closed ? 'CLOSED' : 'OPEN'}
                      tone={year.closed ? 'neutral' : 'success'}
                      label={year.closed ? 'Closed' : 'Open'}
                    />
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => actions.closeFiscalYear.mutate({
                        id: year.id, closed: !year.closed,
                      })}
                    >
                      {year.closed ? 'Reopen' : 'Close'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
            Payroll calendar
          </h3>
          {calendar.isPending && <Skeleton className="m-4 h-16" />}
          {calendar.isSuccess && calendar.data.length === 0 && (
            <EmptyState
              title="No payroll period"
              description="Generate a year from the payroll screen, or add periods one at a time. The pay day has no default: it is a contract term."
            />
          )}
          {calendar.isSuccess && calendar.data.length > 0 && (
            <ul className="divide-y divide-[var(--color-border)]">
              {calendar.data.slice(0, 12).map((period) => (
                <li key={period.id} className="p-3 text-sm">
                  <p className="font-medium text-[var(--color-heading)]">
                    {period.periodYear}-{String(period.periodMonth).padStart(2, '0')}
                  </p>
                  <p className="text-xs text-[var(--color-muted)]">
                    <DateValue value={period.periodStart} /> – <DateValue value={period.periodEnd} />
                    {period.payDate
                      ? <> · paid <DateValue value={period.payDate} /></>
                      : ' · no pay date set'}
                  </p>
                  {period.cutoffNote && (
                    <p className="text-xs text-[var(--color-muted)]">{period.cutoffNote}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card>
        <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
          Numbering
        </h3>
        <p className="flex items-start gap-2 border-b border-[var(--color-border)] p-3 text-xs text-[var(--color-body)]">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          The next number can be raised but never lowered. Lowering it hands out a reference
          that is already on a document somewhere.
        </p>
        {sequences.isPending && <Skeleton className="m-4 h-16" />}
        {sequences.isSuccess && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">For</th>
                <th className="p-3 text-start font-medium">Next will be</th>
                <th className="p-3 text-start font-medium">Next number</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {sequences.data.map((sequence) => (
                <tr key={sequence.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3">{sequence.purpose}</td>
                  <td className="p-3 font-mono">{sequence.example}</td>
                  <td className="p-3">
                    <Input
                      aria-label={`Next number for ${sequence.purpose}`}
                      type="number"
                      className="w-32"
                      value={sequenceDraft[sequence.id] ?? String(sequence.nextNumber)}
                      onChange={(event) => setSequenceDraft({
                        ...sequenceDraft, [sequence.id]: event.target.value,
                      })}
                    />
                  </td>
                  <td className="p-3 text-end">
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        setError(null)
                        actions.saveSequence.mutate({
                          id: sequence.id,
                          purpose: sequence.purpose,
                          nextNumber: Number(sequenceDraft[sequence.id] ?? sequence.nextNumber),
                        }, {
                          onError: (caught) => setError(
                            toApiProblem(caught)?.detail ?? 'That sequence could not be changed'),
                        })
                      }}
                    >
                      Save
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="mb-2 text-sm font-semibold text-[var(--color-heading)]">
          What talks to this system
        </h3>
        <p className="text-sm text-[var(--color-body)]">
          An integration authenticates as itself rather than as a person, with the scopes you
          choose and a token that lasts fifteen minutes. Webhooks tell another system when
          something changes here, signed so it can prove the message came from this one.
        </p>
        <div className="mt-2 flex flex-wrap gap-4">
          <Link
            to="/settings/api-clients"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
          >
            API clients
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
          <Link
            to="/settings/webhooks"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
          >
            Webhooks and deliveries
            <ExternalLink className="size-3" aria-hidden="true" />
          </Link>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="mb-2 text-sm font-semibold text-[var(--color-heading)]">
          Where the mail server lives
        </h3>
        <p className="text-sm text-[var(--color-body)]">
          Not here. SMTP comes from <code>/etc/smart-hrms/*.env</code>, written by Jenkins
          Credentials at mode 600 and never committed — one mail server per deployment. What is
          configurable here is what belongs to <em>this company</em>: which channels are on, and
          what an email says it is from.
        </p>
        <Link
          to="/settings/notifications"
          className="mt-2 inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
        >
          Notification channels and templates
          <ExternalLink className="size-3" aria-hidden="true" />
        </Link>
      </Card>
    </div>
  )
}
